import type { StoreBootstrap, StoreState } from "./sloiStore";

type ApiEnvelope<T> = {
  ok: boolean;
  data?: T;
  state?: StoreState;
  order?: unknown;
  analytics?: StoreState["analytics"];
  message?: string;
  code?: string;
  details?: unknown;
};

export class StoreApiError extends Error {
  code: string;
  details?: unknown;

  constructor(message: string, code = "API_ERROR", details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

let csrfToken = "";

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiEnvelope<T>> {
  const response = await fetch(`/api/store${path}`, {
    ...options,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...options.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as ApiEnvelope<T>;
  if (!response.ok || !payload.ok) {
    throw new StoreApiError(
      payload.message || "Не удалось выполнить действие",
      payload.code,
      payload.details,
    );
  }
  return payload;
}

export const sloiApi = {
  async bootstrap() {
    const payload = await request<StoreBootstrap>("/bootstrap");
    if (!payload.data) throw new StoreApiError("Сервер вернул пустой каталог");
    csrfToken = payload.data.csrfToken;
    return payload.data;
  },

  async event(type: "product_view" | "add_to_cart" | "checkout_start", productId = "") {
    return request<never>("/events", {
      method: "POST",
      body: JSON.stringify({ type, productId }),
    });
  },

  async createOrder(payload: {
    customer: { name: string; email: string; phone: string; city: string; address: string };
    delivery: "courier" | "pickup";
    payment: "card" | "on-receipt";
    items: Array<{ productId: string; size: string; quantity: number }>;
  }) {
    return request<never>("/orders", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async publishSnapshot(state: StoreState) {
    return request<never>("/admin/snapshot", {
      method: "PUT",
      body: JSON.stringify({
        products: state.products,
        banners: state.banners,
        orders: state.orders.map(({ id, status }) => ({ id, status })),
      }),
    });
  },

  async saveProfile(profile: StoreState["customer"]) {
    return request<never>("/account", {
      method: "PUT",
      body: JSON.stringify(profile),
    });
  },

  async subscribe(email: string) {
    return request<never>("/subscribe", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  async reset() {
    const payload = await request<StoreBootstrap>("/admin/reset", { method: "POST" });
    if (!payload.data) throw new StoreApiError("Не удалось сбросить магазин");
    csrfToken = payload.data.csrfToken;
    return payload.data;
  },
};
